import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Modal from "apollo-react/components/Modal";
import Box from "apollo-react/components/Box";
import Typography from "apollo-react/components/Typography";
import CircularProgress from "apollo-react/components/CircularProgress";
import Divider from "apollo-react/components/Divider";
import CreditCardBack from "apollo-react-icons/CreditCardBack";
import axiosInstance from "../../axiosInstance";
import { saveDraft } from "../../utils/draftStorage";
import "./PaymentConfirmationModal.css";

const PAYMOB_IFRAME_BASE_URL =
  "https://accept.paymob.com/api/acceptance/iframes";

const PaymentConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  workflowVersionId,
  requestName,
  draftData,
  isSubmitting = false,
  submissionState = {}, // New prop to access submission state for request_id
}) => {
  const { t } = useTranslation();
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isValidating, setIsValidating] = useState(false); // New state for validation phase
  const [processingMessage, setProcessingMessage] = useState("");
  const [error, setError] = useState(null);

  // Fixed pricing (display only; must match backend DISPLAY envs)
  const BASE_PRICE_USD = Number(
    process.env.REACT_APP_PAYMENT_DISPLAY_BASE_USD || 10
  );
  const TAX_PERCENT = Number(process.env.REACT_APP_PAYMENT_TAX_PERCENT || 14);
  const CURRENCY = process.env.REACT_APP_PAYMENT_DISPLAY_CURRENCY || "USD";

  const calculateFixedDisplayPrice = useCallback(() => {
    const baseCents = Math.round(BASE_PRICE_USD * 100);
    const taxCents = Math.round((baseCents * TAX_PERCENT) / 100);
    const totalCents = baseCents + taxCents;
    return {
      currency: CURRENCY,
      basePrice: baseCents / 100,
      taxPercent: TAX_PERCENT,
      tax: taxCents / 100,
      total: totalCents / 100,
      totalCents,
      // keep these keys for UI compatibility
      submissionFee: 0,
      subtotal: baseCents / 100,
    };
  }, [BASE_PRICE_USD, TAX_PERCENT, CURRENCY]);

  // Calculate price on mount
  useEffect(() => {
    const calculatePrice = async () => {
      if (!isOpen) return;

      setIsCalculating(true);
      setError(null);

      try {
        setPriceBreakdown(calculateFixedDisplayPrice());
      } catch (err) {
        console.error("Error calculating price:", err);
        setPriceBreakdown(null);
      } finally {
        setIsCalculating(false);
      }
    };

    calculatePrice();
  }, [isOpen, calculateFixedDisplayPrice]);

  const handleConfirmPayment = useCallback(async () => {
    setError(null);
    setIsValidating(true); // Show button loading state

    try {
      // Step 1: Try to create request via the submit flow FIRST (this validates)
      // Don't show processing state yet - wait for validation to pass
      const submissionResult = await onConfirm();

      // Check if submission failed (validation errors or other issues)
      // submitAllForms returns false on failure, or { success: false } from handleConfirmPayment
      if (!submissionResult) {
        // Validation failed - toast is already shown by submitAllForms
        // Don't show error in modal, just stop processing
        setIsValidating(false);
        return;
      }

      // Check for explicit success: false
      if (
        typeof submissionResult === "object" &&
        submissionResult.success === false
      ) {
        // Validation failed - toast is already shown by submitAllForms
        // Don't show error in modal, just stop processing
        setIsValidating(false);
        return;
      }

      // Validation passed - NOW show processing state
      setIsValidating(false);
      setIsProcessing(true);

      // Step 2: Save draft for recovery on failure
      setProcessingMessage(t("payment.savingBackupData"));
      const draftResult = saveDraft(
        {
          ...draftData,
          workflowVersionId,
          isPendingPayment: true,
        },
        requestName
      );

      if (draftResult.success) {
        localStorage.setItem("payment_temp_draft_key", draftResult.draftId);
      }

      // Step 3: Get request_id from submission result
      // The submission returns the request data which contains request_id
      let requestId = null;

      // Helper to check if value is a valid ID (number or numeric string)
      const isValidId = (val) =>
        val !== null &&
        val !== undefined &&
        val !== true &&
        val !== false &&
        !isNaN(parseInt(val, 10));

      // Debug logging
      console.log("=== Payment: Extracting request_id ===");
      console.log("submissionResult:", submissionResult);
      console.log("submissionResult type:", typeof submissionResult);

      // Check multiple possible locations for request_id
      // 1. Direct from handleConfirmPayment return: { success: true, requestId, request_id, data }
      if (
        typeof submissionResult === "object" &&
        isValidId(submissionResult.request_id)
      ) {
        requestId = parseInt(submissionResult.request_id, 10);
        console.log(
          "Found request_id at submissionResult.request_id:",
          requestId
        );
      } else if (
        typeof submissionResult === "object" &&
        isValidId(submissionResult.requestId)
      ) {
        requestId = parseInt(submissionResult.requestId, 10);
        console.log(
          "Found request_id at submissionResult.requestId:",
          requestId
        );
      }
      // 2. Check in nested data object (from details.js handleConfirmPayment)
      else if (
        typeof submissionResult === "object" &&
        submissionResult.data &&
        isValidId(submissionResult.data.request_id)
      ) {
        requestId = parseInt(submissionResult.data.request_id, 10);
        console.log(
          "Found request_id at submissionResult.data.request_id:",
          requestId
        );
      }
      // 3. Check if submissionResult IS the response data directly
      else if (
        typeof submissionResult === "object" &&
        isValidId(submissionResult.id)
      ) {
        requestId = parseInt(submissionResult.id, 10);
        console.log("Found request_id at submissionResult.id:", requestId);
      }
      // 4. Fallback to submissionState from context
      else if (
        isValidId(submissionState?.data?.endUserFilledForm?.request_id)
      ) {
        requestId = parseInt(
          submissionState.data.endUserFilledForm.request_id,
          10
        );
        console.log("Found request_id from submissionState:", requestId);
      }

      console.log("Final extracted requestId:", requestId);

      if (!requestId || isNaN(requestId)) {
        // For demo purposes, if we can't get the request_id, show success and redirect to my-requests
        console.warn(
          "Could not get valid request_id from submission, redirecting to my-requests"
        );
        console.log("submissionState:", submissionState);
        setProcessingMessage(t("payment.requestCreatedRedirecting"));
        setTimeout(() => {
          window.location.href = "/customer/requests/my-requests?tab=0";
        }, 1500);
        return;
      }

      // Update draft with request ID
      saveDraft(
        {
          ...draftData,
          workflowVersionId,
          requestId,
          isPendingPayment: true,
        },
        requestName
      );

      // Step 4: Initiate payment with Paymob
      setProcessingMessage(t("payment.initiatingPayment"));
      const paymentResponse = await axiosInstance.post(
        "/api/payments/initiate",
        {
          request_id: requestId,
        }
      );

      const { payment_token, order_id, iframe_id } = paymentResponse.data;

      // Store payment info for processing page
      localStorage.setItem("payment_order_id", order_id);
      localStorage.setItem("payment_request_id", requestId.toString());

      // Step 5: Redirect to Paymob iframe
      setProcessingMessage(t("payment.redirecting"));
      const iframeUrl = `${PAYMOB_IFRAME_BASE_URL}/${iframe_id}?payment_token=${payment_token}`;

      // Redirect the entire window to Paymob
      window.location.href = iframeUrl;
    } catch (err) {
      console.error("Payment confirmation error:", err);
      setError(
        err.response?.data?.message || err.message || "An error occurred"
      );
      setIsValidating(false);
      setIsProcessing(false);
      setProcessingMessage("");
    }
  }, [
    onConfirm,
    draftData,
    workflowVersionId,
    requestName,
    submissionState,
    t,
  ]);

  const formatCurrency = (amount) => {
    return `${amount.toFixed(2)} ${CURRENCY}`;
  };

  if (isProcessing) {
    return (
      <Modal
        open={isOpen}
        onClose={() => {}} // Prevent closing during processing
        title={t("payment.processing")}
        className="payment-confirmation-modal"
        hideButtons={true}
        hideHeader={true}
      >
        <Box className="payment-processing-overlay">
          <CircularProgress variant="indeterminate" size={60} />
          <Typography variant="body1" className="processing-message">
            {processingMessage}
          </Typography>
          <Typography variant="caption" className="processing-warning">
            {t("payment.doNotClose")}
          </Typography>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={t("payment.confirmTitle")}
      className="payment-confirmation-modal"
      message={
        <Box className="payment-modal-content">
          {/* Header */}
          <Box className="payment-modal-header">
            <CreditCardBack className="payment-icon" />
            <Typography variant="body1">
              {t("payment.confirmMessage")}
            </Typography>
          </Box>

          {error && (
            <Box className="payment-error-banner">
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          )}

          {/* Price Breakdown */}
          <Box className="price-breakdown-section">
            <Typography variant="h6" className="section-title">
              {t("payment.paymentSummary")}
            </Typography>

            {isCalculating ? (
              <Box className="price-loading">
                <CircularProgress variant="indeterminate" size={24} />
                <Typography variant="body2">
                  {t("payment.calculating")}
                </Typography>
              </Box>
            ) : priceBreakdown ? (
              <>
                <Box className="price-row">
                  <Typography variant="body2">
                    {t("payment.basePrice")}
                  </Typography>
                  <Typography variant="body2" className="price-value">
                    {formatCurrency(priceBreakdown.basePrice)}
                  </Typography>
                </Box>

                <Divider className="price-divider" />

                <Box className="price-row">
                  <Typography variant="body2">
                    {t("payment.subtotal")}
                  </Typography>
                  <Typography variant="body2" className="price-value">
                    {formatCurrency(priceBreakdown.subtotal)}
                  </Typography>
                </Box>

                <Box className="price-row">
                  <Typography variant="body2">
                    {t("payment.tax")} ({priceBreakdown.taxPercent}%)
                  </Typography>
                  <Typography variant="body2" className="price-value">
                    {formatCurrency(priceBreakdown.tax)}
                  </Typography>
                </Box>

                <Divider className="price-divider" />

                <Box className="price-row total-row">
                  <Typography variant="body1" className="total-label">
                    {t("payment.total")}
                  </Typography>
                  <Typography variant="h5" className="total-value">
                    {formatCurrency(priceBreakdown.total)}
                  </Typography>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="error">
                {t("payment.unableToCalculatePrice")}
              </Typography>
            )}
          </Box>

          {/* Info Notice */}
          <Box className="payment-info-notice">
            <Typography variant="caption">{t("payment.infoNotice")}</Typography>
          </Box>
        </Box>
      }
      buttonProps={[
        {
          label: t("Cancel"),
          onClick: onClose,
          disabled: isSubmitting || isProcessing || isValidating,
        },
        {
          label: isValidating
            ? t("payment.validating")
            : isSubmitting
            ? t("payment.processing")
            : t("payment.proceedToPayment"),
          onClick: handleConfirmPayment,
          disabled:
            isSubmitting ||
            isProcessing ||
            isValidating ||
            isCalculating ||
            !priceBreakdown,
          variant: "contained",
          color: "primary",
        },
      ]}
    />
  );
};

export default PaymentConfirmationModal;
